'use client';

import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { I18nProvider } from '@/i18n/intl';
import { Providers } from '@/components/shared/Providers';
import { Toaster } from '@/components/ui/sonner';
import { LayoutWrapper } from '@/components/layout/LayoutWrapper';
import { SettingsProvider } from '@/components/shared/SettingsProvider';
import { getSettings } from '@/lib/actions/settings';
import { getServices } from '@/lib/actions/services';
import { getFootprints } from '@/lib/actions/footprints';
import { getServiceCategories } from '@/lib/actions/service-categories';
import { getInsights } from '@/lib/actions/insights';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Simisi&Co.',
  url: 'https://simisi.id',
  logo: 'https://simisi.id/images/simisi/logo-simisi.svg',
  sameAs: [
    'https://www.linkedin.com/company/simisi/',
    'https://www.instagram.com/simisi.co/',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+62-819-0568-1347',
    contactType: 'customer service',
    email: 'info@simisi.id',
  },
};

export function RootLayout() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [services, setServices] = useState<any[]>([]);
  const [footprints, setFootprints] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [insights, setInsights] = useState<any[]>([]);

  useEffect(() => {
    void Promise.all([
      getSettings(),
      getServices(),
      getFootprints(),
      getServiceCategories(),
      getInsights(),
    ]).then(([s, svc, fp, cat, ins]) => {
      setSettings(s);
      setServices(svc);
      setFootprints(fp);
      setCategories(cat);
      setInsights(ins);
    });
  }, []);

  return (
    <I18nProvider>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <SettingsProvider settings={settings}>
        <Providers>
          <LayoutWrapper
            settings={settings}
            header={
              <Header
                services={services}
                footprints={footprints}
                categories={categories}
                settings={settings}
                insights={insights}
              />
            }
            footer={<Footer settings={settings} />}
          >
            <Outlet />
          </LayoutWrapper>
          <Toaster />
        </Providers>
      </SettingsProvider>
    </I18nProvider>
  );
}
